import { OptionType } from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { fullstorySelectors, SpaceRequestActions } from '@fullstory/redux';
import { useXhrRequest } from '@fullstory/redux-util';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useActiveSpaceContext } from '../../contexts';

export const UNTITLED_SECTION_ID = 'UNTITLED';
export const UNTITLED_SECTION_LABEL = intl.formatMessage({
  defaultMessage: 'Untitled Section',
  id: '4arOFG',
});
/**
 * Helper for fetching and formulating a list of Space section options.
 *
 * A spaceId must be present, and enabled true, to fetch.
 *
 * @param spaceId The spaceId to fetch sections for
 * @param enabled Explicit enabled check
 */
export const useSpaceSectionOptions = (spaceId?: string, enabled = true) => {
  const { activeSpace } = useActiveSpaceContext();
  const [selected, setSelected] = useState<OptionType<string> | null>(null);
  const orgId = useSelector(fullstorySelectors.getOrgId);

  const spaceSectionRequest = useMemo(
    () =>
      !!spaceId && enabled ? SpaceRequestActions.getSpaceCardView({ id: spaceId, orgId }) : null,
    [enabled, orgId, spaceId],
  );

  // as the request updates this will refetch
  const { data: spaceSectionsResponse, loading, isReady } = useXhrRequest(spaceSectionRequest);

  const options: OptionType<string>[] | null = useMemo(() => {
    // ensure section select is disabled while waiting for new section results
    if (loading) {
      return null;
    }

    // if we have an empty response and it's not refetching or re-loading (eg if the spaceId changed),
    // return a pseudo Section that can later be created on the fly if selected.
    if (spaceSectionsResponse?.sections.length === 0 && !loading) {
      return [
        {
          key: UNTITLED_SECTION_ID,
          value: UNTITLED_SECTION_ID,
          label: UNTITLED_SECTION_LABEL,
        },
      ];
    }

    return spaceSectionsResponse?.sections
      ? spaceSectionsResponse?.sections.map(s => ({ label: s.name, key: s.id, value: s.id }))
      : null;
  }, [loading, spaceSectionsResponse?.sections]);

  // preselect the section if available in the active space context,
  // only attempt to do this once after options have first loaded
  const initialized = useRef(false);
  useEffect(() => {
    if (isReady && !initialized.current && activeSpace?.sectionId) {
      const section = options?.find(o => o.value === activeSpace.sectionId);
      if (section) {
        setSelected(section);
      }
    }
  }, [isReady, activeSpace?.sectionId, options]);
  useEffect(() => {
    if (isReady) {
      initialized.current = true;
    }
  }, [isReady]);

  return {
    options,
    selected,
    setSelected,
  };
};
